import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { AlertTriangle, Mail, MessageCircle, Send } from 'lucide-react'

interface Props {
  /** 'product' | 'lesson_package' — usado na rota que marca a data da solicitação. */
  mappableType: 'product' | 'lesson_package'
  mappableId: number
  /** Texto padronizado gerado no servidor (FiscalRequestMessage). */
  subject: string
  body: string
  /** Vendas já concluídas esperando este cadastro — dá urgência ao botão. */
  pendingSalesCount?: number
  variant?: 'default' | 'outline'
  size?: 'sm' | 'default'
}

/**
 * Envia ao BPO o pedido de cadastro fiscal de um item, por WhatsApp ou e-mail.
 *
 * O texto vem pronto do servidor — WhatsApp e e-mail usam exatamente a mesma
 * mensagem. O clique também registra a data da solicitação no Bewave, para a
 * escola saber desde quando está esperando.
 */
export default function ShareFiscalRequestButton({
  mappableType,
  mappableId,
  subject,
  body,
  pendingSalesCount = 0,
  variant,
  size = 'sm',
}: Props) {
  const [open, setOpen] = useState(false)
  const urgent = pendingSalesCount > 0

  const markShared = () => {
    router.post(
      `/settings/fiscal_mappings/${mappableType}/${mappableId}/share`,
      {},
      { preserveScroll: true, preserveState: true, only: [] },
    )
  }

  const shareVia = (href: string) => {
    markShared()
    globalThis.open(href, '_blank', 'noopener,noreferrer')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant ?? (urgent ? 'default' : 'outline')}
          size={size}
        >
          {urgent ? (
            <AlertTriangle className="h-4 w-4 mr-1.5" />
          ) : (
            <Send className="h-4 w-4 mr-1.5" />
          )}
          Compartilhar solicitação
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent"
          onClick={() =>
            shareVia(`https://wa.me/?text=${encodeURIComponent(body)}`)
          }
        >
          <MessageCircle className="h-4 w-4" /> Enviar por WhatsApp
        </button>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent"
          onClick={() =>
            shareVia(
              `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
            )
          }
        >
          <Mail className="h-4 w-4" /> Enviar por e-mail
        </button>
      </PopoverContent>
    </Popover>
  )
}
